import { css, keyframes, type SerializedStyles } from '@emotion/react';

import { styleguideColors, styleguideFontSize as FONT_SIZE, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT, withOpacity } from '@kaiizen/kai-shiki/core';

const typingBlink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0.25; }
`;

const recBlink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0.2; }
`;

export type VideoToolboxChatStyles = {
  clipbar: SerializedStyles;
  clipbarStatus: SerializedStyles;
  chat: SerializedStyles;
  thread: SerializedStyles;
  msgYou: SerializedStyles;
  msgKai: SerializedStyles;
  threadIntro: SerializedStyles;
  threadIntroCaption: SerializedStyles;
  threadIntroBody: SerializedStyles;
  threadIntroEmphasis: SerializedStyles;
  actBlock: SerializedStyles;
  actBlockPending: SerializedStyles;
  actBlockApplied: SerializedStyles;
  actBlockDismissed: SerializedStyles;
  actTop: SerializedStyles;
  actLead: SerializedStyles;
  actPath: SerializedStyles;
  actPathWhere: SerializedStyles;
  actPathWhat: SerializedStyles;
  actPathHow: SerializedStyles;
  actPathSep: SerializedStyles;
  actMeta: SerializedStyles;
  actPhotoPicker: SerializedStyles;
  actPhotoPickerLabel: SerializedStyles;
  actPhotoOptions: SerializedStyles;
  actPhotoOption: SerializedStyles;
  actPhotoOptionSelected: SerializedStyles;
  actPhotoThumb: SerializedStyles;
  actButtons: SerializedStyles;
  actButtonYes: SerializedStyles;
  actButtonNo: SerializedStyles;
  historyRowShell: SerializedStyles;
  shotRow: SerializedStyles;
  shotImage: SerializedStyles;
  shotText: SerializedStyles;
  historyCreatedAt: SerializedStyles;
  typing: SerializedStyles;
  typingDot: SerializedStyles;
  composer: SerializedStyles;
  composerInput: SerializedStyles;
  composerRow: SerializedStyles;
  talkButton: SerializedStyles;
  talkButtonLive: SerializedStyles;
  recDot: SerializedStyles;
  sendButton: SerializedStyles;
  composerHint: SerializedStyles;
};

export const DEFAULT_VIDEO_TOOLBOX_CHAT_STYLES: VideoToolboxChatStyles = {
  clipbar: css`
    label: VIDEO_TOOLBOX_CLIPBAR;
    flex: none;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    padding: 10px 18px;
    background: ${styleguideColors.white};
    border-bottom: 1px solid ${styleguideColors.stroke};
  `,
  clipbarStatus: css`
    label: VIDEO_TOOLBOX_CLIPBAR_STATUS;
    flex: 1;
    min-width: 0;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoMicro)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${styleguideColors.textTertiary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  chat: css`
    label: VIDEO_TOOLBOX_CHAT;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    background: ${styleguideColors.white};
    border-top: 1px solid ${styleguideColors.stroke};
  `,
  thread: css`
    label: VIDEO_TOOLBOX_CHAT_THREAD;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 18px;
    overflow-y: auto;
  `,
  msgYou: css`
    label: VIDEO_TOOLBOX_CHAT_MSG_YOU;
    align-self: flex-end;
    max-width: 78%;
    padding: 10px 12px;
    background: ${styleguideColors.sunken};
    color: ${styleguideColors.ink};
    font-family: ${FONT.body};
    font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
    line-height: 1.4;
    white-space: pre-wrap;
  `,
  msgKai: css`
    label: VIDEO_TOOLBOX_CHAT_MSG_KAI;
    align-self: stretch;
    width: 100%;
    max-width: 100%;
    padding: 2px 0;
    background: none;
    color: ${styleguideColors.ink};
    font-family: ${FONT.body};
    font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
    line-height: 1.4;
    white-space: pre-line;
  `,
  threadIntro: css`
    label: VIDEO_TOOLBOX_CHAT_THREAD_INTRO;
    align-self: stretch;
    width: 100%;
    max-width: 100%;
    padding: 4px 0 6px;
    background: none;
    color: ${styleguideColors.textSecondary};
    font-family: ${FONT.body};
    font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
    line-height: 1.5;
    white-space: normal;
  `,
  threadIntroCaption: css`
    label: VIDEO_TOOLBOX_CHAT_THREAD_INTRO_CAPTION;
    display: block;
    margin: 0 0 7px;
    color: ${styleguideColors.textTertiary};
    font-family: ${FONT.mono};
    font-size: ${pxToRem(9)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.08em;
    text-transform: uppercase;
    line-height: 1.55;
  `,
  threadIntroBody: css`
    label: VIDEO_TOOLBOX_CHAT_THREAD_INTRO_BODY;
    display: block;
  `,
  threadIntroEmphasis: css`
    label: VIDEO_TOOLBOX_CHAT_THREAD_INTRO_EMPHASIS;
    font-weight: ${FONT_WEIGHT.bold};
    color: ${styleguideColors.ink};
  `,
  actBlock: css`
    label: VIDEO_TOOLBOX_CHAT_ACT;
    align-self: stretch;
    width: 100%;
    max-width: 100%;
    padding: 11px 13px;
    margin: 3px 0;
    background: ${styleguideColors.paper};
    border-left: 3px solid ${styleguideColors.ink};
    white-space: normal;
  `,
  actBlockPending: css`
    label: VIDEO_TOOLBOX_CHAT_ACT_PENDING;
    border-left-color: ${styleguideColors.accent};
    background: ${styleguideColors.wash};

    .v2g-act-lead {
      color: ${styleguideColors.ink};
    }

    .v2g-act-what {
      color: ${styleguideColors.ink};
    }
  `,
  actBlockApplied: css`
    label: VIDEO_TOOLBOX_CHAT_ACT_APPLIED;
  `,
  actBlockDismissed: css`
    label: VIDEO_TOOLBOX_CHAT_ACT_DISMISSED;
    opacity: 0.6;
    background: transparent;
    border-left-color: ${styleguideColors.stroke};

    .v2g-act-lead,
    .v2g-act-what {
      color: ${styleguideColors.textTertiary};
    }
  `,
  actTop: css`
    label: VIDEO_TOOLBOX_CHAT_ACT_TOP;
    display: flex;
    align-items: center;
    gap: 10px;
  `,
  actLead: css`
    label: VIDEO_TOOLBOX_CHAT_ACT_LEAD;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(9)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${styleguideColors.textSecondary};
  `,
  actPath: css`
    label: VIDEO_TOOLBOX_CHAT_ACT_PATH;
    margin-top: 5px;
    font-family: ${FONT.body};
    font-size: ${pxToRem(13)};
    line-height: 1.4;
  `,
  actPathWhere: css`
    label: VIDEO_TOOLBOX_CHAT_ACT_PATH_WHERE;
    color: ${styleguideColors.textTertiary};
  `,
  actPathWhat: css`
    label: VIDEO_TOOLBOX_CHAT_ACT_PATH_WHAT;
    font-weight: ${FONT_WEIGHT.bold};
    color: ${styleguideColors.ink};
  `,
  actPathHow: css`
    label: VIDEO_TOOLBOX_CHAT_ACT_PATH_HOW;
    color: ${styleguideColors.textSecondary};
  `,
  actPathSep: css`
    label: VIDEO_TOOLBOX_CHAT_ACT_PATH_SEP;
    padding: 0 5px;
    color: ${styleguideColors.midtone};
    font-weight: ${FONT_WEIGHT.regular};
  `,
  actMeta: css`
    label: VIDEO_TOOLBOX_CHAT_ACT_META;
    display: block;
    margin-top: 6px;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoCaption)};
    line-height: 1.45;
    color: ${styleguideColors.textTertiary};
  `,
  actPhotoPicker: css`
    label: VIDEO_TOOLBOX_CHAT_ACT_PHOTO_PICKER;
    margin-top: 10px;
  `,
  actPhotoPickerLabel: css`
    label: VIDEO_TOOLBOX_CHAT_ACT_PHOTO_PICKER_LABEL;
    display: block;
    margin-bottom: 6px;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(9)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${styleguideColors.textSecondary};
  `,
  actPhotoOptions: css`
    label: VIDEO_TOOLBOX_CHAT_ACT_PHOTO_OPTIONS;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  `,
  actPhotoOption: css`
    label: VIDEO_TOOLBOX_CHAT_ACT_PHOTO_OPTION;
    padding: 0;
    border: 1px solid ${styleguideColors.stroke};
    background: ${withOpacity(styleguideColors.ink, 0.04)};
    cursor: pointer;

    &:hover {
      border-color: ${styleguideColors.ink};
    }
  `,
  actPhotoOptionSelected: css`
    label: VIDEO_TOOLBOX_CHAT_ACT_PHOTO_OPTION_SELECTED;
    border-color: ${styleguideColors.ink};
    box-shadow: 0 0 0 1px ${styleguideColors.ink};
  `,
  actPhotoThumb: css`
    label: VIDEO_TOOLBOX_CHAT_ACT_PHOTO_THUMB;
    display: block;
    width: 52px;
    height: 52px;
    overflow: hidden;

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  `,
  actButtons: css`
    label: VIDEO_TOOLBOX_CHAT_ACT_BUTTONS;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 9px;
  `,
  actButtonYes: css`
    label: VIDEO_TOOLBOX_CHAT_ACT_BUTTON_YES;
    padding: 6px 14px;
    border: 1px solid ${styleguideColors.ink};
    background: ${styleguideColors.ink};
    color: ${styleguideColors.white};
    cursor: pointer;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(9)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.08em;
    text-transform: uppercase;

    &:hover {
      filter: brightness(1.1);
    }
  `,
  actButtonNo: css`
    label: VIDEO_TOOLBOX_CHAT_ACT_BUTTON_NO;
    padding: 6px 14px;
    border: 1px solid ${styleguideColors.midtone};
    background: none;
    color: ${styleguideColors.ink};
    cursor: pointer;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(9)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.08em;
    text-transform: uppercase;

    &:hover {
      border-color: ${styleguideColors.ink};
    }
  `,
  historyRowShell: css`
    label: VIDEO_TOOLBOX_CHAT_HISTORY_ROW_SHELL;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
    width: 100%;
    min-width: 0;
  `,
  shotRow: css`
    label: VIDEO_TOOLBOX_CHAT_SHOT_ROW;
    align-self: stretch;
    width: 100%;
    max-width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 5px 0;
    background: none;
  `,
  shotImage: css`
    label: VIDEO_TOOLBOX_CHAT_SHOT_IMAGE;
    flex: none;
    width: 44px;
    height: 44px;
    border: 1px solid ${styleguideColors.stroke};
    background: ${withOpacity(styleguideColors.ink, 0.06)};
    overflow: hidden;

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  `,
  shotText: css`
    label: VIDEO_TOOLBOX_CHAT_SHOT_TEXT;
    min-width: 0;
    font-family: ${FONT.body};
    font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
    line-height: 1.4;
    color: ${styleguideColors.ink};
  `,
  historyCreatedAt: css`
    label: VIDEO_TOOLBOX_CHAT_HISTORY_CREATED_AT;
    display: block;
    padding: 0 2px;
    text-align: right;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(9)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.08em;
    color: ${styleguideColors.textTertiary};
    line-height: 1.35;
  `,
  typing: css`
    label: VIDEO_TOOLBOX_CHAT_TYPING;
    align-self: flex-start;
    display: inline-flex;
    gap: 4px;
    padding: 6px 0;
  `,
  typingDot: css`
    label: VIDEO_TOOLBOX_CHAT_TYPING_DOT;
    width: 5px;
    height: 5px;
    background: ${styleguideColors.midtone};
    animation: ${typingBlink} 1s steps(1, end) infinite;

    &:nth-of-type(2) {
      animation-delay: 0.18s;
    }

    &:nth-of-type(3) {
      animation-delay: 0.36s;
    }
  `,
  composer: css`
    label: VIDEO_TOOLBOX_COMPOSER;
    flex: none;
    border-top: 1px solid ${styleguideColors.stroke};
    padding: 10px 18px 14px;
    background: ${styleguideColors.white};
  `,
  composerInput: css`
    label: VIDEO_TOOLBOX_COMPOSER_INPUT;
    width: 100%;
    box-sizing: border-box;
    min-height: 62px;
    resize: vertical;
    padding: 9px 10px;
    border: 1px solid ${styleguideColors.stroke};
    background: ${styleguideColors.shell};
    color: ${styleguideColors.ink};
    font-family: ${FONT.body};
    font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
    line-height: 1.45;

    &:focus {
      outline: none;
      border-color: ${styleguideColors.ink};
    }

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  `,
  composerRow: css`
    label: VIDEO_TOOLBOX_COMPOSER_ROW;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
  `,
  talkButton: css`
    label: VIDEO_TOOLBOX_TALK_BUTTON;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 10px;
    border: 1px solid ${styleguideColors.stroke};
    background: ${styleguideColors.white};
    color: ${styleguideColors.ink};
    cursor: pointer;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoMicro)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.08em;
    text-transform: uppercase;

    &:hover:not(:disabled) {
      border-color: ${styleguideColors.ink};
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  `,
  talkButtonLive: css`
    label: VIDEO_TOOLBOX_TALK_BUTTON_LIVE;
    background: ${styleguideColors.error};
    border-color: ${styleguideColors.error};
    color: ${styleguideColors.white};

    svg {
      display: none;
    }
  `,
  recDot: css`
    label: VIDEO_TOOLBOX_REC_DOT;
    width: 7px;
    height: 7px;
    background: ${styleguideColors.white};
    animation: ${recBlink} 0.9s steps(1, end) infinite;
  `,
  sendButton: css`
    label: VIDEO_TOOLBOX_SEND_BUTTON;
    margin-left: auto;
    padding: 7px 14px;
    border: 1px solid ${styleguideColors.ink};
    background: ${styleguideColors.ink};
    color: ${styleguideColors.white};
    cursor: pointer;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoMicro)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.08em;
    text-transform: uppercase;

    &:disabled {
      opacity: 0.35;
      cursor: default;
    }
  `,
  composerHint: css`
    label: VIDEO_TOOLBOX_COMPOSER_HINT;
    margin-top: 7px;
    font-family: ${FONT.body};
    font-size: ${pxToRem(FONT_SIZE.bodyMicro)};
    color: ${styleguideColors.textTertiary};
    line-height: 1.35;
  `,
};
